// The front door on the line: `game.open(options)` (PLAN-CLIENT-SPLIT §4).
//
// **Everything a mode is, in one call**, so a title menu in either client is
// buttons and nothing else: the opening command, the store's mode row (undo
// obeys it), the drivers seated, the session told who is playing, a room
// joined, a run saved as it plays. The AI client's `App.launch` still does the
// same with React state; this is the engine's copy for the human client and
// the one the AI client moves onto when it is swept.
//
// **Whatever was open is closed first** — a room left connected or a saver
// still subscribed is the previous game reaching into this one.
//
// Not here, deliberately: the URL (`?room=` is a page's address, the client's),
// telemetry rows, and the flags' default ruleset (a client resolves flags; the
// engine opens the default game).

import type { GameMode, LevelName, NewGame, Opened, SavedRun, Side } from "src/api";
import { hasScenario, scenarioById, scenarioOpening } from "src/engine/ai/scenarios";
import { loadRunKindLevel } from "src/engine/campaign/levels";
import type { EngineDrivers } from "src/engine/drivers";
import type { GameStore } from "src/engine/game/GameStore";
import { RUN_CONFIG, type RunKind, runKindOfState, runOpening } from "src/engine/game/modes";
import { clearRun, loadRun, saveRun, savedRun } from "src/engine/game/runSave";
import { freshSeed } from "src/engine/game/seed";
import { RoomClient } from "src/engine/net/RoomClient";
import type { LevelSetup } from "src/engine/rules/campaign/level";
import { type BoardMapId, isBoardMap } from "src/engine/rules/maps";
import { runIsOver } from "src/engine/session/defeat";
import type { Session } from "src/engine/session/Session";

/** What a test injects in place of the network and the level files. */
export interface OpenerOptions {
  /** The parsed level for a run kind. Default: fetched from `/levels/`. */
  levels?: (kind: RunKind) => Promise<LevelSetup | undefined>;
  /** A fresh room id. Default: `/api/room`, behind the site's gate. */
  newRoom?: () => Promise<string>;
  /** A connected room. Default: a `RoomClient` on this store. */
  joinRoom?: (store: GameStore, room: string, name?: string) => RoomClient;
}

export interface Opener {
  open(options: NewGame): Promise<Opened>;
  saved(): SavedRun | null;
  /** Close whatever is open: the room, the saver, the drivers. */
  close(): void;
}

interface OpenerParts {
  store: GameStore;
  drivers: EngineDrivers;
  session: Session;
  attachRoom(client: RoomClient | null): void;
}

const RUN_KIND: Partial<Record<GameMode, RunKind>> = {
  run: "normal",
  "watch-run": "normal",
  training: "training"
};

const defaultNewRoom = async (): Promise<string> => {
  const response = await fetch("/api/room");
  if (!response.ok) throw new Error(response.status === 403 ? "locked" : `room: ${response.status}`);
  const { room } = (await response.json()) as { room?: string };
  if (!room) throw new Error("room: no id");
  return room;
};

const defaultJoinRoom = (store: GameStore, room: string, name?: string): RoomClient => {
  const client = new RoomClient(store, room, undefined, name);
  client.connect();
  return client;
};

export function createOpener(parts: OpenerParts, options: OpenerOptions = {}): Opener {
  const { store, drivers, session } = parts;
  const levels = options.levels ?? loadRunKindLevel;
  const newRoom = options.newRoom ?? defaultNewRoom;
  const joinRoom = options.joinRoom ?? defaultJoinRoom;

  /** The open game's loose ends, closed by the next `open`. */
  let room: RoomClient | null = null;
  let stopSaving: (() => void) | null = null;
  let stopOpening: (() => void) | null = null;
  /** Bumped per `open`, so a level that arrives after a newer open is dropped. */
  let generation = 0;

  const close = (): void => {
    stopSaving?.();
    stopSaving = null;
    stopOpening?.();
    stopOpening = null;
    if (room) {
      parts.attachRoom(null);
      room.close();
      room = null;
    }
    drivers.configure({ enabled: false });
  };

  /**
   * Keep the player's run saved, and drop it once it is over — `App.tsx`'s
   * subscription, word for word: a lost or won run is not one to come back to.
   */
  const saveAsPlayed = (difficulty: LevelName): void => {
    stopSaving = store.subscribe(() => {
      const { state, log } = store.getSnapshot();
      if (!state.run) return;
      if (runIsOver(state)) {
        clearRun();
        return;
      }
      saveRun(state, state.run.level?.levelName ?? RUN_CONFIG.normal.name, difficulty, log);
    });
  };

  /** Seat the machine: `playerSide` is the seat it will not play, null for all of them. */
  const seat = (playerSide: Side | null, level: LevelName, branching: boolean): void => {
    drivers.configure({ enabled: true, playerSide, level, branching, paused: false });
  };

  const refused = (refusal: string): Opened => {
    session.core.setRefusal(refusal);
    return { ok: false, refusal };
  };

  const openRun = async (mode: GameMode, kind: RunKind, o: NewGame, mine: number): Promise<Opened> => {
    const level = o.level ?? "normal";
    let setup: LevelSetup | undefined;
    try {
      setup = await levels(kind);
    } catch (error) {
      return refused(`The level would not load: ${error instanceof Error ? error.message : String(error)}`);
    }
    if (mine !== generation) return { ok: false, refusal: "Another game was opened." };
    let opening;
    try {
      opening = runOpening(kind, { seed: o.seed ?? freshSeed(), hard: level === "hard", level: setup });
    } catch (error) {
      return refused(error instanceof Error ? error.message : String(error));
    }
    store.setMode(mode);
    store.dispatch(opening);
    const watching = mode === "watch-run";
    session.shell({ watching, machinePlays: true, solo: true });
    seat(watching ? null : (0 as Side), level, !watching);
    if (!watching) saveAsPlayed(level);
    return { ok: true, mode };
  };

  const openContinue = (): Opened => {
    const loaded = loadRun();
    if ("failed" in loaded) {
      clearRun();
      return refused(
        loaded.failed === "no-save"
          ? "There is no saved run to continue."
          : "That saved run could not be read, so it has been cleared."
      );
    }
    // A verified log goes in as a log, so undo and history survive (§29 U5).
    if (loaded.log) store.load(loaded.log);
    else store.restore(loaded.state);
    const mode: GameMode = runKindOfState(loaded.state) === "training" ? "training" : "run";
    store.setMode(mode);
    const level = loaded.difficulty ?? "normal";
    session.shell({ machinePlays: true, solo: true });
    seat(0 as Side, level, true);
    saveAsPlayed(level);
    return { ok: true, mode };
  };

  const openScenario = (mode: "solo" | "watch", o: NewGame): Opened => {
    if (!o.scenario || !hasScenario(o.scenario)) {
      return refused(o.scenario ? `There is no scenario "${o.scenario}".` : "Pick a scenario to play.");
    }
    store.setMode(mode);
    store.dispatch(scenarioOpening(scenarioById(o.scenario), o.seed ?? freshSeed(), [], o.teams));
    const watching = mode === "watch";
    session.shell({ watching, machinePlays: true, solo: true, profiles: o.profiles });
    seat(watching ? null : (0 as Side), o.level ?? "hard", !watching);
    if (o.tuning || o.profiles) drivers.configure({ tuning: o.tuning, profiles: o.profiles });
    return { ok: true, mode };
  };

  /** A map by name, refused rather than fallen back from (`boardMap`'s rule). */
  const mapOf = (map: string | undefined): BoardMapId | null =>
    map === undefined ? "classic" : isBoardMap(map) ? map : null;

  const openRoom = async (o: NewGame, mine: number): Promise<Opened> => {
    const map = mapOf(o.map);
    if (!map) return refused(`There is no map "${o.map}".`);
    let id = o.room;
    const creating = !id;
    if (!id) {
      try {
        id = await newRoom();
      } catch (error) {
        const why = error instanceof Error ? error.message : String(error);
        return refused(why === "locked" ? "Sign in to open a room." : "Could not make a room; try again.");
      }
    }
    if (mine !== generation) return { ok: false, refusal: "Another game was opened." };
    store.setMode("net");
    session.shell({});
    const client = joinRoom(store, id, o.name);
    room = client;
    parts.attachRoom(client);
    // **The creator opens the board, once, and only on an empty room** — the
    // map is the room log's first entry, so a joiner replays it and sends
    // nothing (App.tsx's three guards: latch, empty log, online).
    if (creating) {
      let opened = false;
      const tryOpen = (): void => {
        if (opened || client.getState().status !== "online") return;
        opened = true;
        if (store.getSnapshot().log.length === 0) {
          client.send({ type: "new-game", map, seed: o.seed ?? freshSeed() });
        }
      };
      tryOpen();
      if (!opened) stopOpening = client.subscribe(tryOpen);
    }
    return { ok: true, mode: "net", room: id };
  };

  const open = async (o: NewGame): Promise<Opened> => {
    const mine = ++generation;
    close();
    const kind = RUN_KIND[o.mode];
    if (kind) return openRun(o.mode, kind, o, mine);
    switch (o.mode) {
      case "continue":
        return openContinue();
      case "solo":
      case "watch":
        return openScenario(o.mode, o);
      case "net":
        return openRoom(o, mine);
      case "local": {
        // A hot seat: two people, one board, no machine. Named, so the
        // classic map is the versus opening with its cobble rows (§31 Q13).
        const map = mapOf(o.map);
        if (!map) return refused(`There is no map "${o.map}".`);
        store.setMode("local");
        store.dispatch({ type: "new-game", map, seed: o.seed ?? freshSeed() });
        session.shell({});
        return { ok: true, mode: "local" };
      }
    }
    return refused(`"${o.mode}" is not a mode this door opens.`);
  };

  return { open, saved: savedRun, close };
}
